import { Button, Card, CircularProgress, Typography } from '@branchjs/neo-components';
import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import LoggedoutActions from '../../../main/actions/LoggedoutActions';
import { ImageService } from '../../../main/services/ImageService/ImageService';
import LoggedoutStore from '../../../main/stores/LoggedoutStore';
import FullScreenWrapperWithBranchLogo from '../FullScreenWrapperWithBranchLogo';
import ErrorMessageConverter from './ErrorMessageConverter';
import {
  EMAIL_VERIFICATION_IN_PROGRESS,
  EMAIL_VERIFICATION_SUCCESSFUL,
  FAILURE_BODY_MESSAGE,
  FAILURE_BUTTON_TITLE,
  FAILURE_HEADER_SUBTITLE,
  FAILURE_HEADER_TITLE,
  FAILURE_IMAGE_URL,
  SUCCESS_BODY_MESSAGE,
  SUCCESS_BUTTON_TITLE,
  SUCCESS_HEADER_SUBTITLE,
  SUCCESS_HEADER_TITLE,
  SUCCESS_IMAGE_URL,
} from './ScreenVerifyEmail.constants';
import { useStyles } from './ScreenVerifyEmail.styles';

const ScreenVerifyEmail = () => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [loading, setLoading] = useState<boolean>(true);
  const [verified, setVerified] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const classes = useStyles();
  const token = queryParams.get('token');
  const emailVerification = LoggedoutStore.verifyUser(token);

  const handleChange = () => {
    const errors = LoggedoutStore.getErrors();
    const converter = new ErrorMessageConverter(errors);
    setShowError(errors.data ? true : false);
    setErrorMessage(converter.getErrorMessage());
  };

  const handlePrimaryClick = () => {
    if (verified) {
      history.push('/login');
    } else {
      history.push('/');
    }
  };

  useEffect(() => {
    LoggedoutActions.clearErrors();
    LoggedoutStore.addChangeListener(handleChange);

    return () => {
      LoggedoutStore.removeChangeListener(handleChange);
    };
    // biome-ignore lint/correctness/useExhaustiveDependencies: ignored using `--suppress`
  }, [handleChange]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored using `--suppress`
  useEffect(() => {
    if (emailVerification === EMAIL_VERIFICATION_IN_PROGRESS && showError === false) {
      setLoading(true);
    } else if (emailVerification === EMAIL_VERIFICATION_SUCCESSFUL) {
      setLoading(false);
      setVerified(true);
    } else {
      setLoading(false);
      setVerified(false);
    }
  }, [emailVerification, showError, errorMessage]);

  let title = FAILURE_HEADER_TITLE;
  let subTitle = FAILURE_HEADER_SUBTITLE;
  let message = FAILURE_BODY_MESSAGE;
  let buttonTitle = FAILURE_BUTTON_TITLE;
  let imageURL = ImageService.getImageUrl(FAILURE_IMAGE_URL);

  if (verified) {
    title = SUCCESS_HEADER_TITLE;
    subTitle = SUCCESS_HEADER_SUBTITLE;
    message = SUCCESS_BODY_MESSAGE;
    buttonTitle = SUCCESS_BUTTON_TITLE;
    imageURL = ImageService.getImageUrl(SUCCESS_IMAGE_URL);
  }

  return (
    <FullScreenWrapperWithBranchLogo>
      <Card className={classes.card}>
        <div className={classes.cardContent}>
          {loading ? (
            <CircularProgress
              variant="indeterminate"
              className={classes.loading}
              size={40}
              id="verify-email__loading-progress"
            />
          ) : (
            <>
              <Typography variant="h3" color="primary" testid="verify-email--header-title">
                {title}
              </Typography>
              <img className={classes.image} src={imageURL} data-testid="verify-email--header-image" />
              <Typography variant="h4" color="textPrimary" className={classes.subTitle}>
                {subTitle}
              </Typography>
              <Typography variant="body2" color="textSecondary" className={classes.message}>
                {message}
              </Typography>
              <div>
                <Button
                  variant="contained"
                  onClick={handlePrimaryClick}
                  testid="verify-email__continue"
                  className={classes.button}
                >
                  {buttonTitle}
                </Button>
              </div>
              <div className={classes.footer}>
                {showError && (
                  <Typography variant="body2" color="error" testid="verify-email__error-message">
                    {errorMessage ?? 'Something went wrong'}
                  </Typography>
                )}
              </div>
            </>
          )}
        </div>
      </Card>
    </FullScreenWrapperWithBranchLogo>
  );
};

export default ScreenVerifyEmail;
